// src/hooks/useTwilioClient.js
import { useState, useEffect, useRef } from "react";
import { Device } from "@twilio/voice-sdk";

export function useTwilioClient() {
  const [clientName, setClientName] = useState("");
  const [callHistory, setCallHistory] = useState([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callTimer, setCallTimer] = useState("00:00");
  const [callStatus, setCallStatus] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [twilioClientInitialized, setTwilioClientInitialized] = useState(false);
  const [callSid, setCallSid] = useState(null);
  const [transcription, setTranscription] = useState([]);
  const [isProcessingTranscript, setIsProcessingTranscript] = useState(false);

  const deviceRef = useRef(null);
  const timerRef = useRef(null);
  const secondsRef = useRef(0);
  const currentCallRef = useRef(null);

  // --- Utils ---
  useEffect(() => {
    let interval;
    if (currentCallRef.current) {
      interval = setInterval(() => {
        const status = currentCallRef.current?.status?.();
        if (status !== callStatus) {
          setCallStatus(status || null);
        }
      }, 300); // chequea cada 300ms
    }
    return () => clearInterval(interval);
  }, [currentCallRef.current]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  };

  const startTimer = () => {
    secondsRef.current = 0;
    setCallTimer("00:00");
    timerRef.current = setInterval(() => {
      secondsRef.current++;
      setCallTimer(formatTime(secondsRef.current));
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    setCallTimer("00:00");
    secondsRef.current = 0;
    if (callSid) {
      startPollingTranscription();
    }
  };

  // --- Device init ---
  const initializeDevice = (accessToken, identity) => {
    setClientName(identity);
    console.log("Inicializando dispositivo");

    const dev = new Device(accessToken, {
      debug: true,
      answerOnBridge: true,
      codecPreferences: ["opus", "pcmu"],
    });

    dev.on("registered", () => {
      console.log("Twilio.Device listo para hacer y recibir llamadas.");
      setTwilioClientInitialized(true);
    });

    dev.on("error", (err) => {
      console.log(`Error Twilio: ${err?.message}`);
      setTwilioClientInitialized(false);
    });

    dev.on("incoming", (call) => {
      console.log(`Llamada entrante de ${call.parameters?.From}`);
      call.on("accept", (callInstance) => {
        setIsCallActive(true);
        startTimer();
        const sid = callInstance.parameters.CallSid;
        console.log("CallSid:", sid);
        setCallSid(sid);
      });
      call.on("disconnect", () => {
        setIsCallActive(false);
        stopTimer();
      });
      call.accept();
    });

    deviceRef.current = dev;
    dev.register();
  };

  const startupClient = async () => {
    console.log("Solicitando Token...");
    try {
      const res = await fetch(
        "https://back-twilio-54644131242.us-central1.run.app/voice-token",
      );
      const { token, identity } = await res.json();
      console.log("Token obtenido.");
      initializeDevice(token, identity);
    } catch (err) {
      console.error(err);
      console.log("Error al obtener token.");
    }
  };

  // --- Outgoing call ---
  const makeOutgoingCall = async (phone, userId, doctorId, userToProcess) => {
    if (!deviceRef.current) {
      console.log("Device no inicializado.");
      return;
    }
    console.log(`Llamando a ${phone}...`);

    const params = {
      To: phone,
      user_id: userId,
      doctor_id: doctorId,
      user_to_process: userToProcess,
    };

    try {
      const call = await deviceRef.current.connect({
        url: "https://back-twilio-54644131242.us-central1.run.app/voice",
        params: params,
      });

      currentCallRef.current = call;
      setCallStatus(call.status());

      call.on("ringing", () => {
        setCallStatus("ringing");
        console.log("Llamada sonando...");
      });

      call.on("accept", (callInstance) => {
        setIsCallActive(true);
        startTimer();
        const sid = callInstance.parameters.CallSid;
        console.log("CallSid:", sid);
        setCallSid(sid);
        console.log("Llamada accept.");
      });

      call.on("disconnect", () => {
        setIsCallActive(false);
        stopTimer();
        console.log("Llamada disconnect.");
      });
    } catch (err) {
      console.error(err);
      console.log("Error al conectar llamada.");
    }
  };

  const hangupCall = () => {
    console.log("finnnnn");

    if (deviceRef.current) {
      deviceRef.current.disconnectAll();
      stopTimer();
      setIsCallActive(false);
      console.log("Colgando...");
    }
  };

  // --- Historial ---
  const fetchCallHistory = async () => {
    console.log("Solicitando historial de llamadas...");
    try {
      const res = await fetch(
        "https://back-twilio-54644131242.us-central1.run.app/call-history",
      );
      const history = await res.json();
      setCallHistory(history || []);
      console.log(`Historial cargado: ${history?.length || 0} registros.`);
    } catch (err) {
      console.log("Error cargando historial.");
    }
  };

  // cleanup
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (deviceRef.current) {
        deviceRef.current.destroy?.();
      }
    };
  }, []);

  function finishClient() {
    console.log("Finalizando Twilio Client...");

    if (deviceRef.current) {
      console.log("Finalizando Twilio Client... dentro");
      deviceRef.current.destroy?.();
      setTwilioClientInitialized(false);
    }
  }

  function updateSpeakerDevice(selectedDevice) {
    console.log("updateSpeakerDevice en useTwilioClient:", deviceRef);
    if (deviceRef.current) {
      deviceRef.current.audio.speakerDevices.set(selectedDevice);
    }
  }

  async function updateRingtoneDevice(selectedDevice) {
    if (deviceRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: selectedDevice },
        });
        stream.getTracks().forEach((track) => track.stop()); // solo para validar acceso
        await deviceRef.current.audio.setInputDevice(selectedDevice);
        console.log("Micrófono cambiado a:", selectedDevice);
      } catch (err) {
        console.error("Error al cambiar micrófono:", err);
      }
    }
  }

  const toggleMute = () => {
    if (currentCallRef.current) {
      console.log("hamtaro", currentCallRef.current);

      const newMuteState = !isMuted;
      currentCallRef.current.mute(newMuteState);
      setIsMuted(newMuteState);
    }
  };

  const startPollingTranscription = () => {
    setIsProcessingTranscript(true);

    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;

      try {
        const res = await fetch(
          `https://back-twilio-54644131242.us-central1.run.app/get-live-transcription/${callSid}`,
        );

        if (res.ok) {
          const data = await res.json();
          setTranscription(data);
          setIsProcessingTranscript(false);
          clearInterval(interval);
        }
      } catch (err) {
        console.log("Aún procesando...");
      }

      if (attempts > 20) {
        clearInterval(interval);
        setIsProcessingTranscript(false);
      }
    }, 5000);
  };

  return {
    callHistory,
    clientName,
    isCallActive,
    callTimer,
    callStatus,
    isMuted,
    twilioClientInitialized,
    callSid,
    transcription,
    isProcessingTranscript,
    toggleMute,
    startupClient,
    finishClient,
    makeOutgoingCall,
    hangupCall,
    fetchCallHistory,
    updateSpeakerDevice,
    updateRingtoneDevice,
  };
}
